import {AfterViewInit, Component, EventEmitter, HostListener, Inject, OnInit} from '@angular/core';
import {StatePersistenceService} from '../service/state.persistence.service';
import {DOCUMENT} from '@angular/common';
import {ProgressIndicatorService, Step} from '../@shared';
import {RfqDataService} from './service/rfq.data.service';

@Component({
    selector: 'app-rfq',
    templateUrl: './rfq.component.html',
    styleUrls: ['./rfq.component.scss', './rfq.blocks.scss']
})
export class RfqComponent implements OnInit, AfterViewInit {
    viewportHeight: number = 0;
    progressIndicator: boolean = true;

    event$: EventEmitter<boolean> = new EventEmitter<boolean>();
    navigation$: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(@Inject(DOCUMENT) public document: Document,
                public state: StatePersistenceService,
                private progressIndicatorService: ProgressIndicatorService,
                public data: RfqDataService) {
        this.state.progress = true;
        progressIndicatorService.event$.subscribe((item: boolean) => this.onProgressIndicatorEvent(item));
        this.navigation$.subscribe((next: boolean): void => {
            if (next) {
                this.next();
            } else {
                this.back();
            }
        });
    }

    ngOnInit(): void {
        this.data.steps = [{}, {}, {}, {}];
        this.data.activeStep = 0;
        this.data.coverage = 50;

        this.data.home = {
            areas: [
                {
                    number: 1,
                    label: 'Bathroom(s)',
                    qty: 1,
                    assumptions: [
                        "Geyser usage per hour to be 3000w",
                        "An electrical geyser typically switches on and off 2 times per hour"
                    ],
                    appliances: [
                        {
                            label: 'Geyser',
                            wattsPh: 3000,
                            factor: 2,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 8,
                            inc: 0.5,
                            usage: 0,
                            storage: 0
                        },
                        {
                            label: 'Heater',
                            wattsPh: 3000,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 2,
                            inc: 0.1,
                            usage: 0,
                            storage: 0
                        }
                    ],
                    usage: 0,
                    storage: 0
                },
                {
                    number: 2,
                    label: 'Bedroom(s)',
                    qty: 1,
                    assumptions: [
                        "Warm blanket usage per hour to be 75w",
                        "A warm blanket typically switches on and off 6 times per hour",
                        "Air conditioning calculations based on a 12,000 BTU unit",
                    ],
                    appliances: [
                        {
                            label: 'Blanket',
                            wattsPh: 75,
                            factor: 6,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 8,
                            inc: 0.2,
                            usage: 0,
                            storage: 0
                        },
                        {
                            label: 'Aircon',
                            wattsPh: 1200,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 8,
                            inc: 0.2,
                            usage: 0,
                            storage: 0
                        }
                    ],
                    usage: 0,
                    storage: 0
                },
                {
                    number: 3,
                    label: 'Living Area',
                    qty: 1,
                    assumptions: [
                        "The wattage of an average 46-inch LED television typically ranges between 60 and 100 watts per hour during active use",
                        "A media center consists of a basic 2.0 or 2.1-channel system without Dolby Atmos or high-powered amplification",
                        "The media center consumes between 20 and 50 watts during active use",
                    ],
                    appliances: [
                        {
                            label: 'Television',
                            wattsPh: 75,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 8,
                            inc: 0.5,
                            usage: 0,
                            storage: 0
                        },
                        {
                            label: 'Media Center',
                            wattsPh: 40,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 8,
                            inc: 0.5,
                            usage: 0,
                            storage: 0
                        }
                    ],
                    usage: 0,
                    storage: 0
                },
                {
                    number: 4,
                    label: 'Outdoor Area',
                    qty: 1,
                    assumptions: [
                        "Electric fence energiser consumes between 2 and 25 watts during operation",
                        "A standard .75kW pool pump is used for the wattage requirement calculations",
                        "A Coefficient of Performance (COP) of 10 is used for pool heat pump calculations together with a 18.7kW model",
                    ],
                    appliances: [
                        {
                            label: 'Pool Pump',
                            wattsPh: 75,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 12,
                            inc: 0.5,
                            usage: 0,
                            storage: 0
                        },
                        {
                            label: 'Heat Pump',
                            wattsPh: 1870,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 12,
                            inc: 0.5,
                            usage: 0,
                            storage: 0
                        },
                        {
                            label: 'Elec. Fence',
                            wattsPh: 12,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 12,
                            inc: 1,
                            usage: 0,
                            storage: 0
                        }
                    ],
                    usage: 0,
                    storage: 0
                },
                {
                    number: 5,
                    label: 'Kitchen',
                    qty: 1,
                    assumptions: [
                        "A small stove burner on a standard electric stove typically ranges from 1,000 to 1,500 watts",
                        "A large stove burner usually consumes 2,000 to 2,500 watts",
                        "A conventional electric oven consumes an average around 2,500 watts for a mid-sized unit",
                        "Calculated input required for a standard electric stove equals 5.8kW per hour",
                        "A typical mid-range microwave model uses approximately 1,200 watts during operation",
                        "A 1,500-watt kettle boiling 1 liter of water might use 50–75 watt-hours total per use"
                    ],
                    appliances: [
                        {
                            label: 'Stove',
                            wattsPh: 5800,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 3,
                            inc: 0.25,
                            usage: 0,
                            storage: 0
                        },
                        {
                            label: 'Microwave',
                            wattsPh: 1200,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 12,
                            inc: 0.25,
                            usage: 0,
                            storage: 0
                        },
                        {
                            label: 'Kettle',
                            wattsPh: 60,
                            factor: 1,
                            daytime: 0,
                            nighttime: 0,
                            min: 0,
                            max: 12,
                            inc: 1,
                            usage: 0,
                            storage: 0
                        }
                    ],
                    usage: 0,
                    storage: 0
                }
            ],
            usage: 0,
            storage: 0
        };

        this.data.steps.forEach((p: Step, i: number): void => {
            let offset: number = i * 100;
            p.offset = offset;
            p.style = 'left: ' + offset + '%;';
        });

        // Coordinates for New York
        const latitude = 25.7566;
        const longitude = 28.1914;
        const url: string = `https://api.sunrisesunset.io/json?lat=${latitude}&lng=${longitude}`;

        fetch(url)
            .then((response: Response) => response.json())
            .then(data => {
                console.log(data);
                this.data.sunrise = data.results?.sunrise;
                this.data.sunset = data.results?.sunset;
            }).catch(error => {
                console.error('Error:', error);
            });
    }

    ngAfterViewInit(): void {
        setTimeout((): void => {
            this.updateViewportHeight();
            this.progressIndicator = false;
            this.state.progress = false;
        }, 600);
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: Event): void {
        this.updateViewportHeight();
    }

    updateViewportHeight(): void {
        this.viewportHeight = window.innerHeight;
        this.event$.emit(true);
    }

    onProgressIndicatorEvent(item: boolean): void {
        this.progressIndicator = item;
    }

    back(): void {
        this.data.activeStep = this.data.activeStep - 1;
        this.data.steps.forEach((p: Step): void => {
            p.offset = p.offset + 100;
            p.style = 'left: ' + p.offset + '%;';
        });
        this.updateViewportHeight();
    }

    next(): void {
        this.data.activeStep = this.data.activeStep + 1;
        this.data.steps.forEach((p: Step): void => {
            p.offset = p.offset - 100;
            p.style = 'left: ' + p.offset + '%;';
        });
        this.updateViewportHeight();
    }

    findActiveStepScrollHeight(): any {
        if (this.data && this.data.steps && this.data.steps[this.data.activeStep].height) {
            return (80 + this.data.steps[this.data.activeStep].height) + 'px';
        }
        return 0;
    }
}
